import { useState, useEffect } from 'react';
import axios from 'axios';
import { Settings, CheckCircle, AlertCircle, Eye, EyeOff, Save, RefreshCw, ExternalLink, Tag, Plus, X, MessageCircle, Sliders, Youtube, Link2, Video, Brain, Edit3, Cpu, Globe, Trash2, Key, Sparkles, ShieldAlert } from 'lucide-react';
import { OnboardingWizard } from '../components/OnboardingWizard';

const API = process.env.REACT_APP_BACKEND_URL;

function DiscordCommandsButton() {
  const [state, setState] = useState('idle');
  const [msg, setMsg] = useState('');
  const register = async () => {
    setState('loading');
    try {
      const { data } = await axios.post(`${API}/api/discord/register-commands`);
      setState('done');
      setMsg(`${data.registered} commands registered: ${data.commands.join(', ')}`);
    } catch (e) {
      setState('error');
      setMsg(e.response?.data?.detail || 'Failed');
    }
  };
  return (
    <div>
      <button onClick={register} disabled={state === 'loading'} data-testid="discord-register-commands-btn"
        className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all hover:opacity-80 disabled:opacity-40"
        style={{ background: state === 'done' ? 'rgba(61,255,160,0.1)' : 'rgba(255,255,255,0.06)', border: `1px solid ${state === 'done' ? 'rgba(61,255,160,0.3)' : 'rgba(255,255,255,0.1)'}`, color: state === 'done' ? '#3dffa0' : 'rgba(255,255,255,0.6)' }}>
        {state === 'loading' ? <RefreshCw size={11} className="animate-spin" /> : state === 'done' ? <CheckCircle size={11} /> : <MessageCircle size={11} />}
        {state === 'loading' ? 'Registering...' : state === 'done' ? 'Commands Registered' : 'Register Slash Commands'}
      </button>
      {msg && <div className="mt-1 text-[9px]" style={{ color: state === 'done' ? '#3dffa0' : '#ff5050' }}>{msg}</div>}
    </div>
  );
}

function TopicWeightsCard() {
  const [weights, setWeights] = useState(null);
  const [newPrimary, setNewPrimary] = useState('');
  const [newSecondary, setNewSecondary] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/api/topics/weights`);
      setWeights(data);
    } catch { }
  };
  useEffect(() => { load(); }, []);

  const addTopic = (type, value) => {
    if (!value.trim()) return;
    const key = type === 'primary' ? 'primary_topics' : 'secondary_topics';
    setWeights(prev => ({ ...prev, [key]: [...(prev?.[key] || []), value.trim()] }));
    if (type === 'primary') setNewPrimary(''); else setNewSecondary('');
  };

  const removeTopic = (type, idx) => {
    const key = type === 'primary' ? 'primary_topics' : 'secondary_topics';
    setWeights(prev => ({ ...prev, [key]: prev[key].filter((_, i) => i !== idx) }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/topics/weights`, weights);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch { }
    finally { setSaving(false); }
  };

  if (!weights) return <div className="llmc-card p-4 text-xs" style={{ color: 'var(--llmc-text-muted)' }}>Loading topic weights...</div>;

  return (
    <div className="llmc-card p-5">
      <div className="flex items-center gap-2 mb-1">
        <Tag size={16} style={{ color: '#7c5efa' }} />
        <h2 className="text-sm font-bold text-white">Topic Weights</h2>
        <div className="ml-auto flex gap-1.5">
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
            style={{ background: 'rgba(124,94,250,0.15)', color: '#a68eff', border: '1px solid rgba(124,94,250,0.3)' }}>
            {weights.primary_weight}% Primary
          </span>
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
            style={{ background: 'rgba(0,212,184,0.12)', color: '#00d4b8', border: '1px solid rgba(0,212,184,0.3)' }}>
            {weights.secondary_weight}% Secondary
          </span>
        </div>
      </div>
      <p className="text-xs mb-3" style={{ color: 'var(--llmc-text-muted)' }}>
        Primary topics fill 80% of calendar slots. Secondary topics fill the remaining 20%. Used by auto-draft and calendar generation.
      </p>

      {/* Weight slider */}
      <div className="mb-4 p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
        <div className="flex items-center justify-between text-[10px] mb-2" style={{ color: 'rgba(255,255,255,0.5)' }}>
          <span className="flex items-center gap-1"><Sliders size={9} /> Content Split</span>
          <span style={{ color: 'rgba(255,255,255,0.3)' }}>Drag to adjust</span>
        </div>
        <input
          type="range"
          min="10"
          max="90"
          step="5"
          value={weights.primary_weight || 80}
          onChange={e => {
            const primary = Number(e.target.value);
            setWeights(prev => ({ ...prev, primary_weight: primary, secondary_weight: 100 - primary }));
          }}
          data-testid="primary-weight-slider"
          className="w-full"
          style={{ accentColor: '#7c5efa', cursor: 'pointer' }}
        />
        <div className="flex justify-between text-[10px] mt-1.5">
          <span style={{ color: '#a68eff', fontWeight: 700 }}>{weights.primary_weight}% Primary</span>
          <span style={{ color: '#00d4b8', fontWeight: 700 }}>{weights.secondary_weight}% Secondary</span>
        </div>
        {/* Visual bar */}
        <div className="mt-2 h-1.5 rounded-full overflow-hidden flex" style={{ background: 'rgba(255,255,255,0.06)' }}>
          <div style={{ width: `${weights.primary_weight}%`, background: 'linear-gradient(90deg, #7c5efa, #9070ff)', borderRadius: '4px 0 0 4px', transition: 'width 0.2s ease' }} />
          <div style={{ flex: 1, background: 'linear-gradient(90deg, #00d4b8, #00b89c)', borderRadius: '0 4px 4px 0' }} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* Primary */}
        <div>
          <div className="text-xs font-bold mb-2" style={{ color: '#a68eff' }}>Primary Topics (80%)</div>
          <div className="space-y-1 mb-2">
            {(weights.primary_topics || []).map((t, i) => (
              <div key={i} className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg"
                style={{ background: 'rgba(124,94,250,0.08)', border: '1px solid rgba(124,94,250,0.2)' }}>
                <span className="flex-1 text-xs text-white">{t}</span>
                <button onClick={() => removeTopic('primary', i)} data-testid={`remove-primary-${i}`}
                  className="hover:opacity-80" style={{ color: 'rgba(124,94,250,0.5)' }}>
                  <X size={11} />
                </button>
              </div>
            ))}
          </div>
          <div className="flex gap-1">
            <input value={newPrimary} onChange={e => setNewPrimary(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addTopic('primary', newPrimary)}
              placeholder="Add primary topic..." data-testid="add-primary-topic-input"
              className="flex-1 llmc-input px-2 py-1.5 text-xs" />
            <button onClick={() => addTopic('primary', newPrimary)} data-testid="add-primary-topic-btn"
              className="p-1.5 rounded-lg" style={{ background: 'rgba(124,94,250,0.2)', color: '#a68eff' }}>
              <Plus size={12} />
            </button>
          </div>
        </div>

        {/* Secondary */}
        <div>
          <div className="text-xs font-bold mb-2" style={{ color: '#00d4b8' }}>Secondary Topics (20%)</div>
          <div className="space-y-1 mb-2">
            {(weights.secondary_topics || []).map((t, i) => (
              <div key={i} className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg"
                style={{ background: 'rgba(0,212,184,0.07)', border: '1px solid rgba(0,212,184,0.2)' }}>
                <span className="flex-1 text-xs text-white">{t}</span>
                <button onClick={() => removeTopic('secondary', i)} data-testid={`remove-secondary-${i}`}
                  className="hover:opacity-80" style={{ color: 'rgba(0,212,184,0.5)' }}>
                  <X size={11} />
                </button>
              </div>
            ))}
          </div>
          <div className="flex gap-1">
            <input value={newSecondary} onChange={e => setNewSecondary(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addTopic('secondary', newSecondary)}
              placeholder="Add secondary topic..." data-testid="add-secondary-topic-input"
              className="flex-1 llmc-input px-2 py-1.5 text-xs" />
            <button onClick={() => addTopic('secondary', newSecondary)} data-testid="add-secondary-topic-btn"
              className="p-1.5 rounded-lg" style={{ background: 'rgba(0,212,184,0.15)', color: '#00d4b8' }}>
              <Plus size={12} />
            </button>
          </div>
        </div>
      </div>

      <button onClick={save} disabled={saving} data-testid="save-topics-btn"
        className="mt-4 w-full flex items-center justify-center gap-2 py-2.5 rounded-xl font-bold text-sm text-white llmc-btn-primary disabled:opacity-40">
        {saving ? <><div className="w-4 h-4 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: '#fff', borderTopColor: 'transparent' }} />Saving...</>
          : saved ? <><CheckCircle size={14} />Saved!</>
          : <><Save size={14} />Save Topic Weights</>}
      </button>
    </div>
  );
}


function BrandVoiceCard() {
  const [voice, setVoice] = useState('');
  const [guardrails, setGuardrails] = useState('');
  const [ruleCount, setRuleCount] = useState(0);
  const [updatedAt, setUpdatedAt] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/onboarding/brand-voice`);
      setVoice(data.brand_voice || '');
      setGuardrails(data.guardrails || '');
      setRuleCount(data.rule_count || 0);
      setUpdatedAt(data.updated_at || '');
    } catch { }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/api/onboarding/brand-voice`, { brand_voice: voice, guardrails });
      setRuleCount(data.rule_count || 0);
      setUpdatedAt(data.updated_at || '');
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch { }
    finally { setSaving(false); }
  };

  const lastUpdated = updatedAt
    ? new Date(updatedAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
    : null;

  return (
    <div className="llmc-card p-5">
      {/* Header */}
      <div className="flex items-center gap-2 mb-1">
        <Sparkles size={16} style={{ color: '#a68eff' }} />
        <h2 className="text-sm font-bold text-white">Brand Voice</h2>
        <div className="ml-auto flex items-center gap-2">
          {ruleCount > 0 && (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
              style={{ background: 'rgba(166,142,255,0.12)', color: '#a68eff', border: '1px solid rgba(166,142,255,0.25)' }}>
              {ruleCount} rule{ruleCount !== 1 ? 's' : ''}
            </span>
          )}
          {lastUpdated && (
            <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.25)' }}>
              Updated {lastUpdated}
            </span>
          )}
        </div>
      </div>
      <p className="text-xs mb-4" style={{ color: 'var(--llmc-text-muted)' }}>
        These rules are automatically injected into every content generation prompt. The AI learns new rules each time you decline content with feedback — or edit them here manually.
      </p>

      {loading ? (
        <div className="text-xs py-3" style={{ color: 'var(--llmc-text-muted)' }}>Loading...</div>
      ) : (
        <div className="space-y-4">
          {/* Brand Voice textarea */}
          <div>
            <div className="flex items-center gap-1.5 mb-1.5">
              <Sparkles size={11} style={{ color: '#a68eff' }} />
              <label className="text-[10px] font-bold uppercase tracking-wider" style={{ color: '#a68eff' }}>
                AI-Learned Writing Rules
              </label>
            </div>
            <textarea
              value={voice}
              onChange={e => setVoice(e.target.value)}
              data-testid="brand-voice-textarea"
              rows={7}
              className="w-full llmc-input px-3 py-2.5 text-xs resize-none font-mono leading-relaxed"
              placeholder={`Add one rule per line. The AI self-learns rules each time you decline content.\n\nExamples:\n• Always open with a provocative question or contrarian claim\n• Include 2-3 specific statistics with sources in every article\n• Use short punchy sentences. Max 2 lines per paragraph\n• Never use buzzwords: synergy, leverage, disrupt, paradigm`}
              style={{ minHeight: 140 }}
            />
            <div className="text-[9px] mt-1" style={{ color: 'rgba(255,255,255,0.2)' }}>
              One rule per line. Rules starting with • are auto-formatted by the self-learning system.
            </div>
          </div>

          {/* Guardrails textarea */}
          <div>
            <div className="flex items-center gap-1.5 mb-1.5">
              <ShieldAlert size={11} style={{ color: '#f0c27f' }} />
              <label className="text-[10px] font-bold uppercase tracking-wider" style={{ color: '#f0c27f' }}>
                Guardrails — What to Avoid
              </label>
            </div>
            <textarea
              value={guardrails}
              onChange={e => setGuardrails(e.target.value)}
              data-testid="brand-guardrails-textarea"
              rows={5}
              className="w-full llmc-input px-3 py-2.5 text-xs resize-none font-mono leading-relaxed"
              placeholder={`Define hard boundaries for the AI.\n\nExamples:\n• Never mention competitor names directly\n• Never make specific ROI or financial guarantees\n• Avoid political commentary or divisive language\n• Do not use passive voice — always write actively`}
              style={{ minHeight: 100 }}
            />
          </div>

          <button onClick={save} disabled={saving} data-testid="save-brand-voice-btn"
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl font-bold text-sm text-white llmc-btn-primary disabled:opacity-40">
            {saving
              ? <><div className="w-4 h-4 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: '#fff', borderTopColor: 'transparent' }} />Saving...</>
              : saved
                ? <><CheckCircle size={14} />Saved!</>
                : <><Save size={14} />Save Brand Voice</>}
          </button>
        </div>
      )}
    </div>
  );
}


function CustomLLMCard() {
  const [models, setModels] = useState([]);
  const [form, setForm] = useState({ display: '', openrouter_id: '', family: 'custom', description: '', api_key: '' });
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/api/llm/models-for-selector`);
      setModels(data.custom || []);
    } catch { }
  };

  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.display.trim() || !form.openrouter_id.trim()) return;
    setSaving(true); setError('');
    try {
      await axios.post(`${API}/api/llm/models`, {
        id: form.openrouter_id.split('/').pop().replace(/[^a-z0-9-]/gi, '-').toLowerCase(),
        openrouter_id: form.openrouter_id.trim(),
        display: form.display.trim(),
        family: form.family || 'custom',
        description: form.description.trim(),
        ...(form.api_key.trim() ? { api_key: form.api_key.trim() } : {}),
      });
      setSaved(true);
      setForm({ display: '', openrouter_id: '', family: 'custom', description: '', api_key: '' });
      load();
      sessionStorage.removeItem('ideaworks_models_cache');
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to add model');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (modelId) => {
    try {
      await axios.delete(`${API}/api/llm/models/${encodeURIComponent(modelId)}`);
      load();
      sessionStorage.removeItem('ideaworks_models_cache');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to remove model');
    }
  };

  return (
    <div className="llmc-card p-5">
      <div className="flex items-center gap-2 mb-1">
        <Cpu size={16} style={{ color: '#f0c27f' }} />
        <h2 className="text-sm font-bold text-white">Custom LLM Models</h2>
        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full ml-auto"
          style={{ background: 'rgba(240,194,127,0.12)', color: '#f0c27f', border: '1px solid rgba(240,194,127,0.25)' }}>
          {models.length} added
        </span>
      </div>
      <p className="text-xs mb-4" style={{ color: 'var(--llmc-text-muted)' }}>
        Add any OpenRouter-compatible model. It will appear in the model selector across all tools. Optionally provide your own API key for direct access.
      </p>

      {models.length > 0 && (
        <div className="mb-4 space-y-1.5">
          {models.map(m => (
            <div key={m.id} className="flex items-center gap-2 px-3 py-2 rounded-xl"
              style={{ background: 'rgba(240,194,127,0.05)', border: '1px solid rgba(240,194,127,0.15)' }}>
              <Cpu size={12} style={{ color: '#f0c27f' }} />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-white">{m.display}</div>
                <div className="text-[10px] font-mono" style={{ color: 'rgba(255,255,255,0.3)' }}>{m.openrouter_id}</div>
              </div>
              {m.has_own_key && (
                <span className="text-[9px] px-1.5 py-0.5 rounded-full"
                  style={{ background: 'rgba(61,255,160,0.1)', color: '#3dffa0', border: '1px solid rgba(61,255,160,0.2)' }}>
                  own key
                </span>
              )}
              <button onClick={() => remove(m.id)} data-testid={`remove-custom-model-${m.id}`}
                className="p-1 rounded-lg hover:opacity-80"
                style={{ color: 'rgba(255,100,100,0.5)', background: 'rgba(255,100,100,0.05)' }}>
                <Trash2 size={12} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="space-y-2.5 p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
        <div className="text-[10px] font-bold uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.3)' }}>Add New Model</div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] mb-1 block" style={{ color: 'rgba(255,255,255,0.4)' }}>Display Name *</label>
            <input value={form.display} onChange={e => setForm(p => ({ ...p, display: e.target.value }))}
              placeholder="e.g. GPT-4o Mini" data-testid="custom-model-display-input"
              className="w-full llmc-input px-2.5 py-1.5 text-xs" />
          </div>
          <div>
            <label className="text-[10px] mb-1 block" style={{ color: 'rgba(255,255,255,0.4)' }}>OpenRouter Model ID *</label>
            <input value={form.openrouter_id} onChange={e => setForm(p => ({ ...p, openrouter_id: e.target.value }))}
              placeholder="e.g. openai/gpt-4o-mini" data-testid="custom-model-id-input"
              className="w-full llmc-input px-2.5 py-1.5 text-xs font-mono" />
          </div>
        </div>
        <div>
          <label className="text-[10px] mb-1 block" style={{ color: 'rgba(255,255,255,0.4)' }}>Description (optional)</label>
          <input value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
            placeholder="e.g. Fast, cheap drafts" data-testid="custom-model-desc-input"
            className="w-full llmc-input px-2.5 py-1.5 text-xs" />
        </div>
        <div>
          <label className="text-[10px] mb-1 flex items-center gap-1" style={{ color: 'rgba(255,255,255,0.4)' }}>
            <Key size={9} /> API Key (optional — uses platform key if blank)
          </label>
          <div className="relative">
            <input type={showKey ? 'text' : 'password'} value={form.api_key}
              onChange={e => setForm(p => ({ ...p, api_key: e.target.value }))}
              placeholder="sk-or-v1-... or your provider key" data-testid="custom-model-key-input"
              className="w-full llmc-input px-2.5 py-1.5 pr-8 text-xs font-mono" />
            <button type="button" onClick={() => setShowKey(s => !s)}
              className="absolute right-2 top-1/2 -translate-y-1/2 opacity-50 hover:opacity-100"
              style={{ color: 'var(--llmc-text-muted)' }}>
              {showKey ? <EyeOff size={12} /> : <Eye size={12} />}
            </button>
          </div>
          <div className="text-[9px] mt-1" style={{ color: 'rgba(255,255,255,0.25)' }}>
            If provided, calls to this model use your key directly.
          </div>
        </div>
        {error && <div className="text-[11px]" style={{ color: '#fe5252' }}>{error}</div>}
        <button onClick={add} disabled={saving || !form.display.trim() || !form.openrouter_id.trim()}
          data-testid="add-custom-model-btn"
          className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold transition-all hover:opacity-80 disabled:opacity-40"
          style={{ background: 'rgba(240,194,127,0.15)', border: '1px solid rgba(240,194,127,0.3)', color: '#f0c27f' }}>
          {saving ? <RefreshCw size={11} className="animate-spin" /> : <Plus size={11} />}
          {saved ? 'Model Added!' : saving ? 'Adding...' : 'Add Model'}
        </button>
      </div>
      <div className="mt-3 text-[10px]" style={{ color: 'rgba(255,255,255,0.25)' }}>
        Find model IDs at{' '}
        <a href="https://openrouter.ai/models" target="_blank" rel="noreferrer"
          className="underline hover:opacity-80" style={{ color: '#a68eff' }}>openrouter.ai/models</a>
        {' '}— copy the API name (e.g.{' '}
        <code className="px-1 rounded" style={{ background: 'rgba(255,255,255,0.06)' }}>anthropic/claude-3-haiku</code>)
      </div>
    </div>
  );
}


const KEY_GROUPS = [
  {
    label: 'LLM APIs (Priority: Claude → OpenAI → Gemini → OpenRouter)',
    color: '#7c5efa',
    keys: [
      { id: 'anthropic', label: 'Anthropic API Key (Claude Opus)', placeholder: 'sk-ant-api03-...', convex_var: 'CLAUDE_API_KEY_LLMC' },
      { id: 'openai', label: 'OpenAI API Key (GPT-5)', placeholder: 'sk-...', convex_var: 'OPENAI_API_KEY_LLMC' },
      { id: 'google', label: 'Google API Key (Gemini 2.5)', placeholder: 'AIza...', convex_var: 'GOOGLE_GEMINI_API_KEY_LLMC' },
      { id: 'openrouter', label: 'OpenRouter API Key (fallback)', placeholder: 'sk-or-...', convex_var: 'OPENROUTER_API_KEY_LLMC' },
    ],
  },
  {
    label: 'Notifications',
    color: '#00d4b8',
    keys: [
      { id: 'resend', label: 'Resend API Key (Email approvals)', placeholder: 're_...', convex_var: 'RESEND_API_KEY' },
      { id: 'discord', label: 'Discord Webhook URL (Discord approvals)', placeholder: 'https://discord.com/api/webhooks/...', convex_var: 'DISCORD_WEBHOOK_URL' },
      { id: 'slack', label: 'Slack Bot Token (Slack approvals)', placeholder: 'xoxb-...', convex_var: 'SLACK_BOT_TOKEN' },
    ],
  },
  {
    label: 'Research & Scraping (Optional)',
    color: '#f0c27f',
    keys: [
      { id: 'firecrawl', label: 'Firecrawl API Key', placeholder: 'fc-...', convex_var: 'FIRECRAWL_API_KEY' },
      { id: 'serper', label: 'Serper API Key (Web search)', placeholder: '...', convex_var: 'SERPER_API_KEY' },
    ],
  },
];

export default function SettingsPage() {
  const [keyStatus, setKeyStatus] = useState({});
  const [keyInputs, setKeyInputs] = useState({});
  const [showKeys, setShowKeys] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showWizard, setShowWizard] = useState(false);
  const [onboardingProfile, setOnboardingProfile] = useState(null);

  const loadStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/api/keys/status`);
      setKeyStatus(data.status || {});
    } catch { }
  };

  const loadOnboarding = async () => {
    try {
      const { data } = await axios.get(`${API}/api/onboarding/profile`);
      setOnboardingProfile(data.profile);
    } catch { }
  };

  useEffect(() => { loadStatus(); loadOnboarding(); }, []);

  const save = async () => {
    const keysToSave = {};
    for (const [k, v] of Object.entries(keyInputs)) {
      if (v && v.trim()) keysToSave[k] = v.trim();
    }
    if (Object.keys(keysToSave).length === 0) return;
    setSaving(true);
    try {
      await axios.post(`${API}/api/keys/save`, { keys: keysToSave });
      setSaved(true);
      setKeyInputs({});
      loadStatus();
      setTimeout(() => setSaved(false), 3000);
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const hasInputs = Object.values(keyInputs).some(v => v?.trim());
  const llmAvailable = keyStatus.anthropic || keyStatus.openai || keyStatus.google || keyStatus.openrouter;
  const [desktopMode, setDesktopMode] = useState(() => localStorage.getItem('ideaworks_desktop_mode') === 'true');

  const toggleDesktopMode = () => {
    const next = !desktopMode;
    setDesktopMode(next);
    localStorage.setItem('ideaworks_desktop_mode', String(next));
  };

  return (
    <div className="h-full overflow-y-auto" style={{ background: 'var(--llmc-bg)' }}>
      <div className="max-w-3xl mx-auto p-6 flex flex-col gap-5">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.06)' }}>
            <Settings size={20} style={{ color: 'rgba(255,255,255,0.5)' }} />
          </div>
          <div>
            <h1 className="font-black text-white text-lg">Platform Settings</h1>
            <p className="text-xs" style={{ color: 'var(--llmc-text-muted)' }}>Configure API keys · Convex integration · LLM routing</p>
          </div>
          <button onClick={loadStatus} className="ml-auto p-1.5 rounded-lg llmc-btn-ghost">
            <RefreshCw size={14} style={{ color: 'rgba(255,255,255,0.4)' }} />
          </button>
        </div>

        {/* LLM Status Banner */}
        <div className="p-4 rounded-xl"
          style={{
            background: llmAvailable ? 'rgba(61,255,160,0.06)' : 'rgba(235,87,87,0.08)',
            border: `1px solid ${llmAvailable ? 'rgba(61,255,160,0.25)' : 'rgba(235,87,87,0.3)'}`,
          }}>
          <div className="flex items-center gap-2 mb-1">
            {llmAvailable
              ? <CheckCircle size={16} style={{ color: '#3dffa0' }} />
              : <AlertCircle size={16} style={{ color: '#eb5757' }} />}
            <span className="text-sm font-bold" style={{ color: llmAvailable ? '#3dffa0' : '#eb5757' }}>
              {llmAvailable ? 'LLM Available — Platform Fully Operational' : 'No LLM Key Configured — Einstein and AI features offline'}
            </span>
          </div>
          <p className="text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>
            {llmAvailable
              ? 'Claude Opus / GPT-5 / Gemini 2.5 ready. All features operational.'
              : 'Enter at least one LLM API key below. Keys are loaded from Convex at startup, or can be set manually here.'}
          </p>
        </div>

        {/* Convex Info */}
        <div className="llmc-card p-4">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-bold text-white">Convex Integration</h2>
            <a href="https://dashboard.convex.dev" target="_blank" rel="noreferrer"
              className="flex items-center gap-1 text-xs" style={{ color: '#7c5efa' }}>
              Open Dashboard <ExternalLink size={10} />
            </a>
          </div>
          <p className="text-xs mb-3" style={{ color: 'var(--llmc-text-muted)' }}>
            Keys are auto-loaded from Convex at startup. Set keys in:{' '}
            <span className="font-mono" style={{ color: '#a68eff' }}>Convex Dashboard → shiny-dodo-548 → Settings → Environment Variables</span>
          </p>
          <div className="grid grid-cols-2 gap-2 text-xs">
            {[
              { label: 'Deployment', value: 'shiny-dodo-548.convex.cloud', color: '#00d4b8' },
              { label: 'LLM Key Name (Claude)', value: 'CLAUDE_API_KEY_LLMC', color: '#a68eff' },
              { label: 'LLM Key Name (OpenAI)', value: 'OPENAI_API_KEY_LLMC', color: '#a68eff' },
              { label: 'LLM Key Name (Gemini)', value: 'GOOGLE_GEMINI_API_KEY_LLMC', color: '#a68eff' },
            ].map(({ label, value, color }) => (
              <div key={label} className="flex items-center gap-2 px-3 py-2 rounded-lg"
                style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
                <div>
                  <div style={{ color: 'rgba(255,255,255,0.4)' }}>{label}</div>
                  <code style={{ color }}>{value}</code>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Einstein Profile */}
        <div className="llmc-card p-5">
          <div className="flex items-center gap-2 mb-3">
            <Brain size={16} style={{ color: '#a68eff' }} />
            <h2 className="text-sm font-bold text-white">Einstein's Business Profile</h2>
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full ml-auto"
              style={{ background: onboardingProfile ? 'rgba(61,255,160,0.1)' : 'rgba(255,255,255,0.06)', color: onboardingProfile ? '#3dffa0' : 'rgba(255,255,255,0.3)', border: `1px solid ${onboardingProfile ? 'rgba(61,255,160,0.3)' : 'rgba(255,255,255,0.1)'}` }}>
              {onboardingProfile ? 'Configured' : 'Not set'}
            </span>
          </div>
          <p className="text-xs mb-4" style={{ color: 'var(--llmc-text-muted)' }}>
            This context is injected into all content generations to keep everything on-brand.
          </p>
          {onboardingProfile ? (
            <div className="space-y-2 mb-4">
              {onboardingProfile.business_name && (
                <div className="flex gap-2 text-xs"><span style={{ color: 'rgba(255,255,255,0.35)' }}>Business:</span><span className="text-white">{onboardingProfile.business_name}</span></div>
              )}
              {onboardingProfile.target_audience && (
                <div className="flex gap-2 text-xs"><span style={{ color: 'rgba(255,255,255,0.35)' }}>Audience:</span><span className="text-white">{onboardingProfile.target_audience}</span></div>
              )}
              {(onboardingProfile.focus_topics || []).length > 0 && (
                <div className="flex gap-2 text-xs flex-wrap"><span style={{ color: 'rgba(255,255,255,0.35)' }}>Topics:</span><span className="text-white">{onboardingProfile.focus_topics.join(', ')}</span></div>
              )}
              <div className="flex gap-2 text-xs"><span style={{ color: 'rgba(255,255,255,0.35)' }}>Tone / Style:</span><span className="text-white">{onboardingProfile.tone} · {onboardingProfile.style}</span></div>
            </div>
          ) : (
            <div className="text-xs mb-4 p-3 rounded-xl" style={{ background: 'rgba(124,94,250,0.06)', color: 'rgba(255,255,255,0.45)', border: '1px solid rgba(124,94,250,0.2)' }}>
              No profile set yet. Run the setup wizard to help Einstein understand your business.
            </div>
          )}
          <button onClick={() => setShowWizard(true)} data-testid="edit-einstein-profile-btn"
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all hover:opacity-80"
            style={{ background: 'rgba(124,94,250,0.15)', border: '1px solid rgba(124,94,250,0.3)', color: '#a68eff' }}>
            <Edit3 size={13} /> {onboardingProfile ? 'Edit Profile' : 'Run Setup Wizard'}
          </button>
        </div>

        {/* Topic Weights */}
        <TopicWeightsCard />

        {/* Brand Voice */}
        <BrandVoiceCard />

        {/* Discord App Configuration */}
        <div className="llmc-card p-5">
          <div className="flex items-center gap-2 mb-1">
            <MessageCircle size={16} style={{ color: '#7289da' }} />
            <h2 className="text-sm font-bold text-white">Discord App Configuration</h2>
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full ml-auto"
              style={{ background: 'rgba(114,137,218,0.15)', color: '#7289da', border: '1px solid rgba(114,137,218,0.3)' }}>
              2-Way Approval
            </span>
          </div>
          <p className="text-xs mb-4" style={{ color: 'var(--llmc-text-muted)' }}>
            Configure your Discord app to enable approve/decline buttons in Discord for content approval workflows.
          </p>

          <div className="grid grid-cols-2 gap-2 mb-4">
            {[
              { label: 'Client ID', value: '1479278521695080468', color: '#7289da' },
              { label: 'Public Key', value: '08f1db2a…c198a', color: '#7289da' },
              { label: 'Interactions Endpoint URL', value: `${API}/api/discord/interactions`, color: '#00d4b8' },
              { label: 'OAuth2 Redirect URI', value: `${API}/api/discord/callback`, color: '#00d4b8' },
            ].map(({ label, value, color }) => (
              <div key={label} className="p-3 rounded-xl"
                style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <div className="text-[10px] mb-1" style={{ color: 'rgba(255,255,255,0.35)' }}>{label}</div>
                <code className="text-[10px] font-mono" style={{ color, wordBreak: 'break-all', lineHeight: 1.5 }}>{value}</code>
              </div>
            ))}
          </div>

          <div className="text-[10px] leading-relaxed p-3 rounded-xl mb-3"
            style={{ background: 'rgba(114,137,218,0.06)', border: '1px solid rgba(114,137,218,0.15)', color: 'rgba(255,255,255,0.4)' }}>
            <strong style={{ color: '#7289da' }}>Setup steps:</strong><br />
            1. Add <code style={{ color: '#7289da' }}>DISCORD_WEBHOOK_URL</code> to your Convex secrets for outgoing notifications.<br />
            2. In your Discord App settings → General → paste the Interactions Endpoint URL above.<br />
            3. Install the bot using the link below, then content approvals will appear as Discord messages with Approve/Decline buttons.
          </div>

          <div className="flex flex-wrap gap-2">
            <a
              href="https://discord.com/oauth2/authorize?client_id=1479278521695080468"
              target="_blank"
              rel="noreferrer"
              className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all hover:opacity-80"
              style={{ background: 'rgba(114,137,218,0.12)', border: '1px solid rgba(114,137,218,0.3)', color: '#7289da' }}
              data-testid="discord-install-btn"
            >
              Install Discord Bot <ExternalLink size={11} />
            </a>
            <DiscordCommandsButton />
          </div>
        </div>

        {/* Custom LLM Models */}
        <CustomLLMCard />

        {/* Desktop / PWA Mode */}
        <div className="llmc-card p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Globe size={16} style={{ color: '#00d4b8' }} />
              <div>
                <h2 className="text-sm font-bold text-white">Desktop / PWA Mode</h2>
                <p className="text-[11px]" style={{ color: 'var(--llmc-text-muted)' }}>
                  When enabled, the app auto-logs in without a password on startup. Ideal for standalone desktop use.
                </p>
              </div>
            </div>
            <button
              onClick={toggleDesktopMode}
              data-testid="toggle-desktop-mode-btn"
              className="relative w-11 h-6 rounded-full transition-all flex-shrink-0"
              style={{ background: desktopMode ? '#00d4b8' : 'rgba(255,255,255,0.1)' }}>
              <span className="absolute top-0.5 left-0.5 w-5 h-5 rounded-full transition-all"
                style={{ background: '#fff', transform: desktopMode ? 'translateX(20px)' : 'translateX(0)' }} />
            </button>
          </div>
          {desktopMode && (
            <div className="mt-2 text-[10px] px-3 py-2 rounded-lg"
              style={{ background: 'rgba(0,212,184,0.07)', border: '1px solid rgba(0,212,184,0.2)', color: '#00d4b8' }}>
              Auto-login active — reload the app on a new session to trigger passwordless login.
            </div>
          )}
        </div>

        {/* LinkedIn Publishing */}
        <LinkedInSettings />

        {/* Manual Key Override */}
        <div className="llmc-card p-5">
          <h2 className="text-sm font-bold text-white mb-1">Manual Key Override</h2>
          <p className="text-xs mb-4" style={{ color: 'var(--llmc-text-muted)' }}>
            Enter keys here to override Convex. Keys are injected into the runtime environment immediately.
          </p>

          {KEY_GROUPS.map(group => (
            <div key={group.label} className="mb-5">
              <div className="flex items-center gap-2 mb-3">
                <span className="w-2 h-2 rounded-full" style={{ background: group.color }} />
                <span className="text-xs font-bold" style={{ color: group.color }}>{group.label}</span>
              </div>
              <div className="flex flex-col gap-2.5">
                {group.keys.map(({ id, label, placeholder, convex_var }) => {
                  const isSet = keyStatus[id];
                  const showVal = showKeys[id];
                  return (
                    <div key={id}>
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-xs font-medium" style={{ color: 'rgba(255,255,255,0.6)' }}>{label}</label>
                        <div className="flex items-center gap-1.5">
                          <span className="text-[10px] font-mono" style={{ color: 'rgba(255,255,255,0.3)' }}>{convex_var}</span>
                          {isSet && <CheckCircle size={11} style={{ color: '#3dffa0' }} />}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <input
                          type={showVal ? 'text' : 'password'}
                          value={keyInputs[id] || ''}
                          onChange={e => setKeyInputs(prev => ({ ...prev, [id]: e.target.value }))}
                          className="flex-1 llmc-input px-3 py-2 text-xs font-mono"
                          data-testid={`key-input-${id}`}
                          placeholder={isSet ? '●●●●●●● (currently set)' : placeholder}
                        />
                        <button onClick={() => setShowKeys(prev => ({ ...prev, [id]: !prev[id] }))}
                          className="px-2 rounded-lg llmc-btn-ghost" style={{ opacity: 0.5 }}>
                          {showVal ? <EyeOff size={14} /> : <Eye size={14} />}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          <button onClick={save} disabled={saving || !hasInputs}
            data-testid="save-keys-btn"
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-bold text-white text-sm llmc-btn-primary disabled:opacity-40">
            {saving
              ? <><div className="w-4 h-4 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: '#fff', borderTopColor: 'transparent' }} />Saving...</>
              : saved
                ? <><CheckCircle size={16} />Saved!</>
                : <><Save size={16} />Save Keys to Runtime</>}
          </button>
        </div>

        {/* Architecture info */}
        <div className="llmc-card p-4 text-xs" style={{ color: 'var(--llmc-text-muted)' }}>
          <div className="font-semibold text-white mb-1.5">LLM Priority Chain</div>
          <div className="flex items-center gap-2 flex-wrap">
            {['Claude Opus (claude-opus-4-6)', 'GPT-5.2', 'Gemini 2.5 Pro', 'OpenRouter (fallback)'].map((m, i) => (
              <div key={m} className="flex items-center gap-1">
                {i > 0 && <span style={{ color: 'rgba(255,255,255,0.2)' }}>→</span>}
                <span className="px-2 py-0.5 rounded-full"
                  style={{ background: 'rgba(124,94,250,0.1)', border: '1px solid rgba(124,94,250,0.2)', color: '#a68eff' }}>
                  {m}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* YouTube Connection */}
        <YouTubeConnectionCard />
      </div>

      {/* Edit profile wizard */}
      {showWizard && (
        <OnboardingWizard
          editMode={true}
          onComplete={() => { setShowWizard(false); loadOnboarding(); }}
        />
      )}
    </div>
  );
}

function YouTubeConnectionCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);

  const checkStatus = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/youtube/status?brand_id=default`);
      setStatus(data);
    } catch { }
    setLoading(false);
  };

  useEffect(() => {
    checkStatus();
    // Handle OAuth callback
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const ytCallback = params.get('youtube_callback');
    if (code && ytCallback) {
      axios.post(`${API}/api/youtube/oauth/exchange?code=${code}&brand_id=default`)
        .then(() => { checkStatus(); window.history.replaceState({}, '', '/settings'); })
        .catch(() => {});
    }
  }, []);

  const connectYouTube = async () => {
    setConnecting(true);
    try {
      const { data } = await axios.post(`${API}/api/youtube/oauth/init?brand_id=default`);
      if (data.auth_url) window.location.href = data.auth_url;
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to start YouTube OAuth');
    }
    setConnecting(false);
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect YouTube account?')) return;
    await axios.post(`${API}/api/youtube/oauth/disconnect?brand_id=default`);
    checkStatus();
  };

  return (
    <div className="llmc-card p-5">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: 'rgba(255,0,0,0.15)' }}>
          <Youtube size={16} style={{ color: '#FF0000' }} />
        </div>
        <div>
          <h2 className="text-sm font-bold text-white">YouTube Channel</h2>
          <p className="text-[11px]" style={{ color: 'var(--llmc-text-muted)' }}>Connect for video uploads, analytics, and channel monitoring</p>
        </div>
        <div className="ml-auto">
          {status?.oauth_connected
            ? <span className="flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full" style={{ background: 'rgba(61,255,160,0.12)', color: '#3dffa0', border: '1px solid rgba(61,255,160,0.3)' }}>
                <CheckCircle size={11} /> Connected
              </span>
            : <span className="flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full" style={{ background: 'rgba(255,255,255,0.06)', color: 'var(--llmc-text-muted)' }}>
                <AlertCircle size={11} /> Not connected
              </span>
          }
        </div>
      </div>

      {loading ? (
        <div className="text-xs" style={{ color: 'var(--llmc-text-muted)' }}>Checking status...</div>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="rounded-xl p-2.5" style={{ background: 'rgba(255,255,255,0.04)' }}>
              <div className="text-[10px] mb-0.5" style={{ color: 'var(--llmc-text-muted)' }}>Data API</div>
              <div className="flex items-center gap-1.5 font-medium" style={{ color: status?.api_key_configured ? '#3dffa0' : '#f97316' }}>
                {status?.api_key_configured ? <CheckCircle size={11} /> : <AlertCircle size={11} />}
                {status?.api_key_configured ? 'Configured' : 'Missing key'}
              </div>
            </div>
            <div className="rounded-xl p-2.5" style={{ background: 'rgba(255,255,255,0.04)' }}>
              <div className="text-[10px] mb-0.5" style={{ color: 'var(--llmc-text-muted)' }}>OAuth Upload</div>
              <div className="flex items-center gap-1.5 font-medium" style={{ color: status?.oauth_connected ? '#3dffa0' : 'var(--llmc-text-muted)' }}>
                {status?.oauth_connected ? <CheckCircle size={11} /> : <Link2 size={11} />}
                {status?.oauth_connected ? status.channel_name || 'Connected' : 'Not connected'}
              </div>
            </div>
          </div>

          {!status?.oauth_connected ? (
            <button onClick={connectYouTube} disabled={connecting} data-testid="connect-youtube-btn"
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold transition-all hover:opacity-80 disabled:opacity-40"
              style={{ background: 'rgba(255,0,0,0.15)', border: '1px solid rgba(255,0,0,0.3)', color: '#FF5555' }}>
              {connecting ? <RefreshCw size={13} className="animate-spin" /> : <Youtube size={13} />}
              {connecting ? 'Redirecting...' : 'Connect YouTube Channel'}
            </button>
          ) : (
            <div className="space-y-2">
              {status.channel_name && (
                <div className="text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>
                  Uploading to: <span className="font-semibold text-white">{status.channel_name}</span>
                  {status.channel_id && (
                    <a href={`https://www.youtube.com/channel/${status.channel_id}`} target="_blank" rel="noreferrer"
                      className="ml-2" style={{ color: '#a68eff' }}>
                      <ExternalLink size={11} />
                    </a>
                  )}
                </div>
              )}
              <button onClick={disconnect} className="text-xs" style={{ color: 'rgba(255,100,100,0.7)' }}>
                Disconnect
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


/**
 * LinkedIn Publishing Configuration
 * Standalone section to configure LinkedIn OAuth token + verify connection.
 */
function LinkedInSettings() {
  const [token, setToken] = useState('');
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showToken, setShowToken] = useState(false);

  const saveToken = async () => {
    if (!token.trim()) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const { data } = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/linkedin/save-token`, { access_token: token });
      setSuccess(data.message || 'LinkedIn token saved!');
      if (data.person_urn) setProfile(p => ({ ...p, person_urn: data.person_urn }));
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to save token');
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    setError('');
    setSuccess('');
    try {
      const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/linkedin/profile`);
      setProfile(data);
      setSuccess(`Connected as ${data.name} (${data.email || data.person_urn})`);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Connection test failed');
      setProfile(null);
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="llmc-card p-5">
      <div className="flex items-center gap-2 mb-3">
        <Link2 size={15} style={{ color: '#0a66c2' }} />
        <h2 className="text-sm font-bold text-white">LinkedIn Publishing</h2>
        {profile && <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold" style={{ background: 'rgba(10,102,194,0.15)', color: '#0a66c2' }}>Connected</span>}
      </div>
      <p className="text-xs mb-4" style={{ color: 'var(--llmc-text-muted)' }}>
        Connect your LinkedIn account to publish articles and posts directly from the platform.{' '}
        <a href="https://developer.linkedin.com/docs/sign-in-with-linkedin/archive" target="_blank" rel="noreferrer"
          className="underline hover:opacity-80" style={{ color: '#0a66c2' }}>
          How to get your access token →
        </a>
      </p>

      {profile && (
        <div className="flex items-center gap-3 mb-4 px-3 py-2.5 rounded-xl" style={{ background: 'rgba(10,102,194,0.08)', border: '1px solid rgba(10,102,194,0.2)' }}>
          {profile.picture && <img src={profile.picture} alt="" className="w-8 h-8 rounded-full" />}
          <div className="min-w-0">
            <div className="text-xs font-semibold text-white">{profile.name}</div>
            <div className="text-[10px]" style={{ color: 'var(--llmc-text-muted)' }}>{profile.email || profile.person_urn}</div>
          </div>
        </div>
      )}

      <div className="flex gap-2 mb-3">
        <div className="relative flex-1">
          <input
            type={showToken ? 'text' : 'password'}
            value={token}
            onChange={e => setToken(e.target.value)}
            placeholder="Paste your LinkedIn OAuth access token..."
            data-testid="linkedin-token-input"
            className="llmc-input w-full px-3 py-2 text-xs pr-8"
          />
          <button onClick={() => setShowToken(s => !s)}
            className="absolute right-2 top-1/2 -translate-y-1/2 opacity-50 hover:opacity-100"
            style={{ color: 'var(--llmc-text-muted)' }}>
            {showToken ? <EyeOff size={12} /> : <Eye size={12} />}
          </button>
        </div>
        <button onClick={saveToken} disabled={!token.trim() || saving}
          data-testid="linkedin-save-token-btn"
          className="px-4 py-2 rounded-xl text-xs font-bold transition-all hover:opacity-80 disabled:opacity-40"
          style={{ background: 'rgba(10,102,194,0.15)', border: '1px solid rgba(10,102,194,0.3)', color: '#0a66c2' }}>
          {saving ? <RefreshCw size={12} className="animate-spin" /> : <Save size={12} />}
        </button>
        <button onClick={testConnection} disabled={testing}
          data-testid="linkedin-test-btn"
          className="px-4 py-2 rounded-xl text-xs font-bold transition-all hover:opacity-80 disabled:opacity-40"
          style={{ background: 'rgba(61,255,160,0.08)', border: '1px solid rgba(61,255,160,0.2)', color: '#3dffa0' }}>
          {testing ? <RefreshCw size={12} className="animate-spin" /> : <CheckCircle size={12} />}
        </button>
      </div>

      {success && <div className="text-[11px] mt-1" style={{ color: '#3dffa0' }}>{success}</div>}
      {error && <div className="text-[11px] mt-1" style={{ color: '#f97316' }}>{error}</div>}

      <div className="mt-3 text-[10px] space-y-1" style={{ color: 'var(--llmc-text-muted)' }}>
        <div>1. Go to <a href="https://developer.linkedin.com/" target="_blank" rel="noreferrer" className="underline" style={{ color: '#0a66c2' }}>LinkedIn Developer Portal</a> → Create App</div>
        <div>2. Request scopes: <code className="px-1 rounded" style={{ background: 'rgba(255,255,255,0.06)' }}>w_member_social openid profile email</code></div>
        <div>3. Generate an access token and paste it above</div>
      </div>
    </div>
  );
}
